import { Globe, X } from "lucide-react";
import * as React from "react";

import { translationsApi } from "@/api/translations";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useUIStore } from "@/stores/ui";

interface Props {
  open: boolean;
  existingLocales: string[];
  onClose: () => void;
  onAdded: (locale: string) => void | Promise<void>;
}

const LOCALE_PRESETS = [
  { code: "vi", name: "Tiếng Việt" },
  { code: "en", name: "English" },
  { code: "ja", name: "日本語" },
  { code: "ko", name: "한국어" },
  { code: "zh-CN", name: "简体中文" },
  { code: "th", name: "ภาษาไทย" },
  { code: "id", name: "Bahasa Indonesia" },
  { code: "fr", name: "Français" },
];

/**
 * AddLanguageDialog — admin tạo locale mới + bootstrap với 1 placeholder
 * key (`_meta.language_name`) để locale xuất hiện trong list_locales.
 */
export function AddLanguageDialog({
  open,
  existingLocales,
  onClose,
  onAdded,
}: Props): JSX.Element | null {
  const pushToast = useUIStore((s) => s.pushToast);
  const [code, setCode] = React.useState("");
  const [name, setName] = React.useState("");
  const [busy, setBusy] = React.useState(false);

  if (!open) return null;

  const handleAdd = async (preset?: { code: string; name: string }): Promise<void> => {
    const eff = preset ?? { code, name };
    if (!eff.code || !/^[a-z]{2}(-[A-Z]{2})?$/.test(eff.code)) {
      pushToast({
        variant: "warning",
        title: "Code locale phải dạng 'vi', 'en', 'zh-CN'",
      });
      return;
    }
    if (existingLocales.includes(eff.code)) {
      pushToast({
        variant: "warning",
        title: `Locale ${eff.code} đã tồn tại`,
      });
      return;
    }
    setBusy(true);
    try {
      await translationsApi.upsert({
        locale: eff.code,
        namespace: "_meta",
        key: "language_name",
        value: eff.name || eff.code,
        description: "Bootstrap entry — không xoá",
      });
      pushToast({ variant: "success", title: `Đã thêm ${eff.code}` });
      await onAdded(eff.code);
      onClose();
      setCode("");
      setName("");
    } catch (e) {
      pushToast({
        variant: "destructive",
        title: "Thêm thất bại",
        description: e instanceof Error ? e.message : String(e),
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="bg-background rounded-lg border shadow-lg max-w-md w-full p-6 space-y-4"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-between">
          <h2 className="font-semibold flex items-center gap-2">
            <Globe className="size-4" /> Thêm ngôn ngữ
          </h2>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground">
            <X className="size-4" />
          </button>
        </header>

        <div>
          <p className="text-xs font-medium mb-2 text-muted-foreground">
            Chọn nhanh từ preset:
          </p>
          <div className="flex flex-wrap gap-1">
            {LOCALE_PRESETS.filter((p) => !existingLocales.includes(p.code)).map(
              (p) => (
                <Button
                  key={p.code}
                  size="sm"
                  variant="outline"
                  disabled={busy}
                  onClick={() => void handleAdd(p)}
                >
                  {p.code} — {p.name}
                </Button>
              ),
            )}
          </div>
        </div>

        <div className="space-y-2 border-t pt-3">
          <p className="text-xs font-medium text-muted-foreground">
            Hoặc nhập custom:
          </p>
          <div className="grid grid-cols-2 gap-2">
            <Input
              value={code}
              onChange={(e) => setCode(e.target.value.toLowerCase())}
              placeholder="vi-VN"
              className="font-mono"
            />
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Display name"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>Huỷ</Button>
          <Button onClick={() => void handleAdd()} disabled={busy || !code}>
            {busy ? "..." : "Thêm"}
          </Button>
        </div>
      </div>
    </div>
  );
}
